import { useState, useEffect, useRef } from 'react'
import { Link } from 'react-router-dom'
import TopNav from '../../components/TopNav'
import LoadError from '../../components/LoadError'
import useCustomerList from '../../utils/useCustomerList'
import { loadErrorMessage } from '../../utils/loadState'
import {
  getConnections, debugDevHubCapture, debugDevHubDrain, debugDevHubDrainStatus,
  startWfmUiLogin, completeWfmUiLogin,
  runProbeOnboarding, getProbeOnboardingStatus, getProbeOnboardingHistory, cancelProbeOnboarding,
} from '../../api'
import { stage7OutcomePlain, classificationPlain } from '../../utils/stage7Outcomes'
import './AdminDevHubCapture.css'

// A running drain is server-side (a FastAPI BackgroundTask) and outlives this
// page's own React state -- but without this, leaving the page (close, reload,
// even a different tab in the same browser) loses the only record of WHICH
// drain to look at. Scott, 2026-09-22: "I left the page and now have no
// visibility to where we are." localStorage is per-browser, not per-device or
// per-account, but it directly covers the reported case: same browser, page
// reloaded or reopened.
const LAST_DRAIN_KEY = 'dhc_last_drain'

function _saveLastDrain(enumFlowId, customerId) {
  try {
    localStorage.setItem(LAST_DRAIN_KEY, JSON.stringify({ enumFlowId, customerId, savedAt: Date.now() }))
  } catch { /* private browsing / storage disabled -- recovery just won't be available */ }
}

function _loadLastDrain() {
  try {
    const raw = localStorage.getItem(LAST_DRAIN_KEY)
    return raw ? JSON.parse(raw) : null
  } catch { return null }
}

function _clearLastDrain(enumFlowId) {
  try {
    const saved = _loadLastDrain()
    if (saved && saved.enumFlowId === enumFlowId) localStorage.removeItem(LAST_DRAIN_KEY)
  } catch { /* ignore */ }
}

// Remembers which customer was last selected, purely for page-reload
// convenience -- it does NOT track "is a run in progress" (item 123 retired
// that: state whose meaning depends on when it was last written is exactly
// the class of bug that cost seven rounds of fixes on 2026-09-24). Whatever
// customer this restores, the customerId-select effect below asks the SERVER
// directly what's actually running for them -- the same source of truth used
// for every other selection, not a special-cased recovery path.
const LAST_PROBE_KEY = 'dhc_last_probe'

function _saveLastProbe(customerId) {
  try {
    localStorage.setItem(LAST_PROBE_KEY, JSON.stringify({ customerId, savedAt: Date.now() }))
  } catch { /* private browsing / storage disabled -- recovery just won't be available */ }
}

function _loadLastProbe() {
  try {
    const raw = localStorage.getItem(LAST_PROBE_KEY)
    return raw ? JSON.parse(raw) : null
  } catch { return null }
}

// Scott, 2026-09-23: "add a way to dump the results being presented on the page to
// a spreadsheet so I don't have to do the F12 crap" -- every number on this page has,
// up to now, only been readable via a browser-console fetch. Same xlsx pattern already
// used by SpineView.jsx (dynamic import -- xlsx is a real dependency, but only loaded
// when an export is actually clicked, not on every page load).
async function _exportSheetsToExcel(filename, sheets) {
  const nonEmpty = sheets.filter(s => s.rows && s.rows.length)
  if (!nonEmpty.length) return
  const XLSX = await import('xlsx')
  const wb = XLSX.utils.book_new()
  for (const { name, rows } of nonEmpty) {
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(rows), name)
  }
  XLSX.writeFile(wb, filename)
}

async function _exportRowsToExcel(filename, rows, sheetName = 'Sheet1') {
  return _exportSheetsToExcel(filename, [{ name: sheetName, rows }])
}

function _fmtClock(epochSeconds) {
  if (!epochSeconds) return '—'
  return new Date(epochSeconds * 1000).toLocaleTimeString()
}

function _fmtDuration(startSeconds, endSeconds) {
  if (!startSeconds) return '—'
  const end = endSeconds || (Date.now() / 1000)
  const secs = Math.max(0, Math.round(end - startSeconds))
  const m = Math.floor(secs / 60)
  const s = secs % 60
  return `${m}m ${s}s`
}

export default function AdminDevHubCapture() {
  const { customers, error: customersError, reload: reloadCustomers } = useCustomerList()
  const [customerId, setCustomerId] = useState('')
  const [targetCategories, setTargetCategories] = useState('')
  const [batchSize, setBatchSize] = useState(10)

  // Scott, 2026-09-25: "I have no idea why both WFM and PRO wouldn't operate from
  // the same set of tools and behave exactly the same way. They should ... or any
  // product for that matter." Run Probe Onboarding below used to hardcode
  // ('UKG', 'UKG_PRO_WFM_REST') at all four of its call sites -- the only reason
  // Pro needed a DIFFERENT page (AdminSchemaDiff.jsx) to probe at all, even though
  // `_discover_bodies`/`run_onboarding_probe` (runner.py) have zero vendor
  // branching in them. `connections` is this customer's REAL, live set of
  // connections (getConnections -- the same list Connectors.jsx already shows),
  // not a hardcoded pair -- so this selector is already correct for whatever
  // vendor/product this customer actually has, not just UKG's two products.
  const [connections, setConnections] = useState([])
  // A failed connections read is an error with a Retry, not a silently missing selector (item 201).
  const [connectionsError, setConnectionsError] = useState(null)
  const [connectionsTry, setConnectionsTry] = useState(0)
  const [probeVendor, setProbeVendor] = useState('UKG')
  const [probeProduct, setProbeProduct] = useState('UKG_PRO_WFM_REST')

  // Advanced mode: a separate Developer Hub / UKG Community credential, stored on the
  // connection specifically for this. Off by default — Scott, 2026-09-22: "I'm not doing
  // shit with the creds. We're going to run this out of the platform itself." The default
  // path below uses the WFM connection's own already-stored login instead.
  const [advancedMode, setAdvancedMode] = useState(false)
  const [docPath, setDocPath] = useState('/wfm/reference')
  const [hostname, setHostname] = useState('')

  const [capturing, setCapturing] = useState(false)
  const [error, setError] = useState(null)
  const [result, setResult] = useState(null)

  // MFA step — every real run against a live tenant has required it so far.
  const [flowId, setFlowId] = useState(null)
  const [needsMfa, setNeedsMfa] = useState(false)
  const [mfaCode, setMfaCode] = useState('')
  const [completing, setCompleting] = useState(false)

  const [draining, setDraining] = useState(false)
  const [drainStatus, setDrainStatus] = useState(null)
  const drainPollRef = useRef(null)
  const [lookupEnumFlowId, setLookupEnumFlowId] = useState('')

  // Stage 7 -- separate from the capture/drain above. This runs the FULL probe
  // harness (auth triage, classification, shape capture) against the customer's
  // live connection, with discover_bodies=true so every needs_body endpoint gets
  // a real chance at a proven request body -- including, as of item 100, a first
  // try seeded from whatever this customer's own captured Dev Hub specs already
  // name, before any live vendor refusal is even needed.
  //
  // ITEM 123, 2026-09-24 -- ARCHITECTURAL FIX, not another patch. Every one of
  // items 116-122 (seven rounds, one day) was the SAME shape of bug: this state
  // used to be THREE separately-mutated pieces (probeRunning/probeStatus/
  // probeHistory), and every action (start, cancel, a poll tick, a page
  // reload) had its own hand-written logic for which of the three to update --
  // so there was always another code path that updated one and forgot another.
  // Scott: "I still don't understand how I'm ever going to onboard a new
  // product with the Probe Harness... update your notes to explicitly state
  // that YOU ARE THE FREAKING ARCHITECT" (see CLAUDE.md §3a).
  //
  // Now there is ONE state object and exactly ONE function (refreshProbe,
  // below) that ever writes it from a GET -- it always fetches BOTH status
  // and history together, atomically, never a partial update. probeRunning
  // is DERIVED from probe.status, not tracked separately, so it cannot
  // disagree with it.
  //
  // REVISED 2026-09-24 (items 129 applied to startProbeRun; cancelRun
  // followed the same principle): "no optimistic patching, always re-read
  // from the server" turned out to be its own bug, not just a simplification
  // -- a GET issued right after a write can race that write's own
  // propagation (FastAPI's BackgroundTasks runs the actual work AFTER the
  // POST response; a Supabase write isn't guaranteed instantly visible to
  // the very next read either). Scott: "a few seconds is not acceptable at
  // all." startProbeRun and cancelRun now synthesize `probe` DIRECTLY from
  // their own action's response the instant it resolves -- that response
  // already proved what happened, synchronously, with no race to lose.
  // refreshProbe (the GET) still runs afterward and on every poll tick to
  // stay current going forward; it is no longer the thing the UI waits on
  // to reflect the action that was just taken.
  //
  // ITEM 136, 2026-09-24 -- items 124/129/130/132 each found and patched ONE
  // caller whose GET could race a synthesized write and clobber it -- and
  // Scott hit the exact same shape again anyway, from a caller none of those
  // covered: a fresh page load's mount-time refreshProbe (the customerId-
  // select effect below) still in flight when he clicked Run Probe
  // Onboarding moments later. Per CLAUDE.md §3a, the fix is not a fifth
  // caller-specific patch -- it is one guard, in the one function every
  // caller already shares.
  //
  // ITEM 139, 2026-09-24 -- item 136's OWN guard was the wrong invariant, and
  // Scott caught it: "you have a logic bug." It compared "did ANYTHING
  // change since I started this particular fetch" -- which only protects a
  // read that was ALREADY in flight the moment a synthesis happened. It does
  // NOTHING for every poll tick AFTER that: watchProbe's first tick, 5s
  // later ("it almost looks like something is killing the job about 5
  // seconds into it" -- exactly the poll interval, not a coincidence),
  // starts its own fresh fetch with nothing pending, so the old check always
  // let it through -- and if the SERVER's own write for the new run hasn't
  // landed yet by then (three sequential Supabase round trips server-side,
  // see routers/connector.py's _run_and_persist_onboarding_probe), that read
  // faithfully reports the PREVIOUS run's leftover status, passes the old
  // check every single time, and clobbers the synthesized state -- for
  // however many 5s ticks it takes the server to catch up (Scott's "30
  // second wait" is just that, measured).
  //
  // The right invariant is not "did something change" -- it is "does this
  // read's own CONTENT match what I already know to be true." Two refs,
  // because start and cancel need asymmetric handling: starting a NEW run
  // makes every OTHER run_id/done combination stale (only one run should be
  // current); cancelling ONE run must NOT discard a read about a DIFFERENT
  // run that's still genuinely active (item 116's multi-run case) -- only a
  // read for the SAME run_id that hasn't yet caught up to done:true.
  const pendingNewRunIdRef = useRef(null)
  const pendingCancelledRunIdRef = useRef(null)
  const [probe, setProbe] = useState({ status: null, history: null })
  const probeRunning = probe.status?.done === false
  // Immediate click feedback ("Starting…") distinct from probeRunning, which
  // only flips once the server has actually confirmed a run exists -- honest
  // in both directions: never claims "running" before it's true (today's
  // whole set of complaints), but still gives instant feedback on the click.
  const [startingRun, setStartingRun] = useState(false)
  const [probeError, setProbeError] = useState(null)
  const probePollRef = useRef(null)
  // Scott, 2026-09-24, live: clicked "Cancel" twice because nothing showed the
  // first click had registered -- that produced two duplicate history entries
  // server-side (see routers/connector.py's _probe_cancel_lock). A ref (not just
  // state) guards the click itself synchronously, the same way probePollRef does
  // above, since two clicks in the same tick would both still see stale state.
  // cancellingKeys (state, mirrors the ref) drives which Cancel button shows
  // "Cancelling…" and is disabled.
  const cancellingRef = useRef(new Set())
  const [cancellingKeys, setCancellingKeys] = useState(() => new Set())

  useEffect(() => () => { if (drainPollRef.current) clearInterval(drainPollRef.current) }, [])
  useEffect(() => () => { if (probePollRef.current) clearInterval(probePollRef.current) }, [])

  // Recover a drain that was already running when this page was closed or
  // reloaded -- watchDrain (defined below, but a `function` declaration is
  // hoisted within this component body) only WATCHES, never restarts, so this
  // can't spawn a second background task racing the original one.
  useEffect(() => {
    const saved = _loadLastDrain()
    if (!saved?.enumFlowId) return
    setCustomerId(saved.customerId || '')
    setResult({ enum_flow_id: saved.enumFlowId, done: false, category_results: [] })
    ;(async () => {
      try {
        const status = await debugDevHubDrainStatus(saved.enumFlowId)
        setDrainStatus(status)
        if (status.done || status.stopped_on_error) {
          _clearLastDrain(saved.enumFlowId)
          return
        }
      } catch {
        // No checkpoint yet, or a transient miss -- watchDrain's own
        // MAX_NOT_STARTED_MISSES tolerance covers this from here on.
      }
      watchDrain(saved.enumFlowId)
    })()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Restore which customer was last viewed after a page reload -- a plain
  // convenience, nothing more. It does NOT try to recover "was a run going";
  // the customerId-select effect below asks the server that directly, the
  // same way it does for any other selection, so there is exactly one path
  // that ever learns "is a run active" instead of a special-cased second one.
  useEffect(() => {
    const saved = _loadLastProbe()
    if (saved?.customerId) setCustomerId(saved.customerId)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // ONE function, called from every place probe state can change (customer
  // select, start, cancel, each poll tick) -- it always reads BOTH endpoints
  // fresh and sets them together in the single `probe` state object. This is
  // the actual fix for items 116-122: there is no second store left for any
  // caller to forget to update, because there is only ever one write, and it
  // is always a full, current read, never a partial or optimistic patch.
  //
  // `forVendor`/`forProduct` are explicit arguments, same discipline as
  // `forCustomerId` -- this function is called from inside a `setInterval`
  // closure (watchProbe) that can outlive several renders, so reading
  // `probeVendor`/`probeProduct` off component state directly here would
  // silently poll the OLD product after the operator switches products
  // mid-run, the exact class of stale-closure bug items 136/139 already
  // exist to prevent.
  async function refreshProbe(forCustomerId, forVendor, forProduct) {
    const [status, history] = await Promise.all([
      getProbeOnboardingStatus(forCustomerId, forVendor, forProduct).catch(() => null),
      getProbeOnboardingHistory(forCustomerId, forVendor, forProduct).catch(() => null),
    ])
    // See pendingNewRunIdRef/pendingCancelledRunIdRef's declaration (item 139).
    // Checked against this read's own CONTENT, not against whether anything
    // changed since the fetch started -- the server can legitimately take
    // several poll cycles to catch up to a write this tab already knows
    // about, and every one of those reads must be discarded, not just the
    // first.
    if (pendingNewRunIdRef.current) {
      if (status?.run_id === pendingNewRunIdRef.current) {
        // The server has caught up to OUR run_id -- trust this read fully
        // whatever `done` says now. Requiring done:false here too would
        // permanently discard every future read for a run that happens to
        // finish before its first poll ever sees it.
        pendingNewRunIdRef.current = null
      } else {
        return { status: null, history: null, stale: true }
      }
    }
    if (pendingCancelledRunIdRef.current && status?.run_id === pendingCancelledRunIdRef.current) {
      if (status?.done === false) {
        return { status: null, history: null, stale: true }
      }
      pendingCancelledRunIdRef.current = null  // the server has caught up
    }
    // A transient history-fetch failure keeps the last-known-good history
    // rather than blanking the table; status has no such fallback because
    // `probeRunning` is derived from it and a stale "still running" is worse
    // than a momentary "unknown".
    setProbe(prev => ({ status, history: history ?? prev.history }))
    return { status, history }
  }

  // This customer's REAL connections -- any vendor, any product, exactly what
  // Connectors.jsx already shows -- not a hardcoded pair. Defaults the probe
  // selector to UKG WFM when this customer has one (matching this page's
  // existing muscle-memory default), else the first connection found, so
  // picking a customer with only a Pro connection doesn't silently point the
  // Run Probe Onboarding button at a product they don't have.
  useEffect(() => {
    if (!customerId) { setConnections([]); return }
    let cancelled = false
    setConnectionsError(null)
    getConnections(customerId).then(conns => {
      if (cancelled) return
      const list = Array.isArray(conns) ? conns : []
      setConnections(list)
      const hasCurrent = list.some(c => c.vendor_id === probeVendor && c.product_id === probeProduct)
      if (!hasCurrent) {
        const preferred = list.find(c => c.vendor_id === 'UKG' && c.product_id === 'UKG_PRO_WFM_REST') || list[0]
        if (preferred) { setProbeVendor(preferred.vendor_id); setProbeProduct(preferred.product_id) }
      }
    }).catch(err => { if (!cancelled) { setConnections([]); setConnectionsError(loadErrorMessage(err)) } })
    return () => { cancelled = true }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerId, connectionsTry])

  // Ask the server what's true for this customer+product whenever either is
  // selected -- covers a fresh customer pick, the reload-restore effect above,
  // AND switching products in the selector below (Scott, 2026-09-25: WFM and
  // Pro "should operate from the same set of tools and behave exactly the same
  // way" -- this is that: the SAME effect, SAME guard, now keyed on product
  // too instead of assuming WFM). Resumes polling if the server says a run is
  // genuinely active; never assumes it from anything this browser tab
  // remembers on its own.
  useEffect(() => {
    // A poll from the PREVIOUS customer/product must never keep writing `probe`
    // after the selection changes -- its closure still points at the old one,
    // and nothing else here would stop it (this existed latently in the
    // pre-item-123 code too; closing it now rather than carrying it forward).
    if (probePollRef.current) { clearInterval(probePollRef.current); probePollRef.current = null }
    // A run_id this tab is still waiting to see confirmed belongs to whichever
    // customer+product was selected when start/cancel set it -- carrying it
    // over to a DIFFERENT selection just made would mean no read for that new
    // selection could ever match it, permanently discarding every read as
    // stale (item 139's guard was never meant to be scoped to one selection
    // forever, since switching selection already tears down and rebuilds
    // everything else).
    pendingNewRunIdRef.current = null
    pendingCancelledRunIdRef.current = null
    if (!customerId) { setProbe({ status: null, history: null }); return }
    _saveLastProbe(customerId)
    let cancelled = false
    refreshProbe(customerId, probeVendor, probeProduct).then(({ status }) => {
      if (!cancelled && status?.done === false) watchProbe(customerId, probeVendor, probeProduct)
    })
    return () => { cancelled = true }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerId, probeVendor, probeProduct])

  // Ambient backstop, independent of whatever specifically triggered polling
  // (click, cancel, customer select). Scott, 2026-09-24: "I had to click the
  // button again for it to tell me that a run was in progress" -- the
  // trigger-based poll SHOULD have already shown that on its own; this is a
  // structural safety net for whatever still lets that state drift, rather
  // than chasing one more specific race. Only acts when nothing is currently
  // being watched (`!probePollRef.current`), so it never fights the real
  // poll loop -- it exists purely to notice if that loop isn't running when
  // it should be, and re-synchronize with the truth without the user having
  // to do anything.
  useEffect(() => {
    if (!customerId) return
    const ambient = setInterval(() => {
      if (probePollRef.current) return
      refreshProbe(customerId, probeVendor, probeProduct).then(({ status }) => {
        if (status?.done === false) watchProbe(customerId, probeVendor, probeProduct)
      })
    }, 15000)
    return () => clearInterval(ambient)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerId, probeVendor, probeProduct])

  // A REAL kill switch, not just hiding a run from the UI -- Scott, 2026-09-23:
  // "I don't care what it would cost me... build the kill switch." Stops the
  // background process from making further vendor requests (services/
  // probe_harness's loops check cancellation once per endpoint and raise
  // RunCancelled), not just abandoning it silently while it keeps running.
  function cancelRun(runId, startedAt) {
    // startedAt disambiguates a runId-less (legacy) cancel server-side -- see
    // routers/connector.py's debug_probe_onboarding_cancel -- so a delayed
    // duplicate request can't land on a different, newer run that has since
    // taken over the same status file.
    const key = runId || `legacy-${startedAt}`
    if (cancellingRef.current.has(key)) return  // already in flight -- the double-click guard
    cancellingRef.current.add(key)
    setCancellingKeys(new Set(cancellingRef.current))
    setProbeError(null)
    cancelProbeOnboarding(customerId, probeVendor, probeProduct, runId, startedAt)
      .then((res) => {
        // CONFIRMED live 2026-09-24: a cancel request can resolve successfully
        // (200, no thrown error) while the BACKEND found nothing to cancel --
        // {"cancelled": [], "note": "no matching in-flight run found"} -- and
        // this handler used to treat that identically to a real cancellation,
        // silently refreshing and moving on. Scott clicked Cancel, saw zero
        // feedback, and the run kept showing "running" with no explanation at
        // all -- because there WAS none shown, even though the response said
        // outright that nothing happened. Must be checked explicitly, the
        // same way startProbeRun already checks started===false.
        if (!res?.cancelled?.length) {
          throw new Error(res?.note || 'The server found no matching in-flight run to cancel.')
        }
        if (probePollRef.current) {
          clearInterval(probePollRef.current)
          probePollRef.current = null
        }
        // Synthesize the cancellation immediately from what THIS response
        // already told us -- same principle startProbeRun now uses, same
        // reason: a follow-up GET can race that write's own propagation
        // (confirmed live for the start path; item 119 traced the same
        // shape of race for cancel specifically). The UI no longer waits on
        // a read to reflect what the action's own response already proved.
        const cancelledIds = new Set(res.cancelled)
        // item 139 -- see pendingCancelledRunIdRef's declaration. Only the
        // run_id actually reflected in `probe.status` needs protecting; a
        // cancelled run_id this tab was never tracking has no UI state to
        // regress.
        if (probe.status && cancelledIds.has(probe.status.run_id)) {
          pendingCancelledRunIdRef.current = probe.status.run_id
        }
        setProbe(prev => ({
          status: prev.status && cancelledIds.has(prev.status.run_id)
            ? { ...prev.status, done: true, cancelled: true, error: prev.status.error || 'cancelled by user' }
            : prev.status,
          history: prev.history ? {
            ...prev.history,
            in_flight: prev.history.in_flight.filter(r => !cancelledIds.has(r.run_id)),
            history: prev.history.history.map(r => cancelledIds.has(r.run_id)
              ? { ...r, done: true, error: r.error || 'cancelled by user' }
              : r),
          } : prev.history,
        }))
        // Still reconcile with the server shortly after -- to pick up
        // anything else that changed (a DIFFERENT run still active, the
        // real finished_at) -- but the UI no longer waits on this round
        // trip to reflect what just happened.
        return refreshProbe(customerId, probeVendor, probeProduct)
      })
      .then(({ status } = {}) => {
        // Cancelling ONE run (item 116's ">1 runs active at once" case) must
        // not stop watching if a DIFFERENT run is still genuinely active --
        // the latest status just read is whichever run that is now.
        if (status?.done === false) watchProbe(customerId, probeVendor, probeProduct)
      })
      .catch(err => setProbeError(err.message || 'Could not cancel the run'))
      .finally(() => {
        cancellingRef.current.delete(key)
        setCancellingKeys(new Set(cancellingRef.current))
      })
  }

  function resetRunState() {
    setError(null); setResult(null); setDrainStatus(null)
    setFlowId(null); setNeedsMfa(false); setMfaCode('')
  }

  // _run_dev_hub_flow (routers/connector.py) catches anything that goes wrong mid-scrape
  // and returns it as `dev_hub_error`, not `error` — a real failure there used to come
  // back looking like an empty, silent "nothing matched" result with no banner at all.
  function captureError(data) {
    return data?.error || data?.dev_hub_error || data?.leaf_operation_download_error || null
  }

  async function runStart() {
    if (!customerId) { setError('Pick a customer first.'); return }
    resetRunState()
    setCapturing(true)
    try {
      const data = advancedMode
        ? await debugDevHubCapture(customerId, {
            targetCategories: targetCategories.trim() || undefined,
            docPath,
            batchSize,
            hostname: hostname.trim() || undefined,
          })
        : await startWfmUiLogin(customerId, {
            targetCategories: targetCategories.trim() || undefined,
            batchSize,
          })
      const capErr = captureError(data)
      if (data?.needs_mfa) {
        setFlowId(data.flow_id)
        setNeedsMfa(true)
        return
      }
      // Show the diagnostics (steps_log/sso_diag/etc.) alongside the error, not instead
      // of it -- a failure mid-login still carries real partial progress worth seeing
      // (which field filled, whether submit was clicked, the URL right before it hung).
      if (capErr) setError(capErr)
      setResult(data)
      // The whole point of a full-population crawl: no manual "Resume" clicking
      // through dozens of batches. If this capture didn't finish in one request,
      // start draining the rest in the background immediately.
      if (data && data.done === false && data.enum_flow_id) {
        resumeDrain(data.enum_flow_id)
      }
    } catch (err) {
      setError(err.message || 'Capture failed')
    } finally {
      setCapturing(false)
    }
  }

  async function runComplete() {
    if (!flowId || !mfaCode.trim()) { setError('Enter the code from the email first.'); return }
    setCompleting(true); setError(null)
    try {
      const data = await completeWfmUiLogin(customerId, flowId, mfaCode.trim())
      if (data?.filled === false) { setError('The code was rejected or the page changed — try starting again.'); return }
      if (data?.looks_logged_in === false) { setError('Login did not complete — check the code and try again.'); return }
      // As in runStart: show the diagnostics alongside a failure, not instead of it --
      // _run_dev_hub_flow only runs after MFA succeeds, so an error from here still
      // carries real steps_log/sso_diag progress worth seeing.
      const capErr = captureError(data)
      if (capErr) setError(capErr)
      setNeedsMfa(false)
      setResult(data)
      if (data && data.done === false && data.enum_flow_id) {
        resumeDrain(data.enum_flow_id)
      }
    } catch (err) {
      setError(err.message || 'Completing the login failed')
    } finally {
      setCompleting(false)
    }
  }

  // Only watches an ALREADY-RUNNING drain -- never (re-)starts one. Split out of
  // resumeDrain so a page reload can reattach to a drain that's still running
  // server-side (a FastAPI BackgroundTask, independent of the browser tab)
  // without calling debug-dev-hub-drain again, which would spawn a SECOND
  // concurrent background task racing the first over the same persisted
  // remaining_categories record. Scott, 2026-09-22: "I left the page and now
  // have no visibility" -- the drain itself was never affected by that, only
  // this browser tab's own poll and enum_flow_id (held in React state, gone on
  // reload) were.
  function watchDrain(enumFlowId) {
    // Called from resumeDrain, the mount-recovery effect, and lookupDrain, none
    // of which coordinate with each other. REFUSING a second call here (the
    // original guard: `if (drainPollRef.current) return`) traded one bug for a
    // worse one -- any path that left drainPollRef set for a PREVIOUS drain (even
    // correctly) silently blocked every future attempt to watch a NEW one, with
    // no error, nothing, forever, until a manual page refresh re-mounted
    // everything from scratch. Confirmed live 2026-09-24 for the probe run's
    // identical pattern (watchProbe, same fix applied there) -- clearing any existing
    // interval before starting a new one gets the ORIGINAL protection (never two
    // intervals racing each other) without ever refusing a legitimate restart.
    if (drainPollRef.current) clearInterval(drainPollRef.current)
    setDraining(true); setError(null)
    // _drain_enum_flow (the background task) doesn't write its first status
    // checkpoint to storage until its first batch finishes, and each category
    // in a batch gets its OWN 60s timeout on the backend
    // (_drill_one_enumerate_batch: asyncio.wait_for(drill(), timeout=60),
    // connector.py:7663) -- so a full first batch's worst case is
    // batchSize x 60s, not a fixed guess. Scott's 2026-09-22 run hit exactly
    // this: the original 150s (30 x 5s) grace period was undersized by up to
    // 4x for batch_size=10. debug-dev-hub-drain-status legitimately 404s
    // ("was never called for it") on every poll before the first checkpoint
    // exists -- not a failure, just "still starting." Tolerate that up to the
    // real worst case (plus buffer for login/navigation overhead) instead of
    // an arbitrary constant.
    const PER_CATEGORY_WORST_CASE_SECONDS = 60
    const STARTUP_OVERHEAD_SECONDS = 60
    const firstBatchWorstCaseSeconds = batchSize * PER_CATEGORY_WORST_CASE_SECONDS + STARTUP_OVERHEAD_SECONDS
    const MAX_NOT_STARTED_MISSES = Math.ceil(firstBatchWorstCaseSeconds / 5)
    let notStartedMisses = 0
    // A single failed poll for ANY other reason (not the "hasn't started yet" 404
    // above) used to kill this loop permanently, even though the drain itself is a
    // server-side BackgroundTask completely unaffected by this browser tab's own
    // network hiccups. Confirmed live 2026-09-23: net::ERR_NETWORK_IO_SUSPENDED (a
    // Chrome/OS network suspension, e.g. the tab backgrounded or the machine's
    // power management throttling it) killed a poll and the page went silent for
    // the rest of a run that was still completing fine server-side. Tolerate a
    // handful of consecutive transient misses before concluding something is
    // actually wrong, same reasoning as the 404 tolerance just above.
    const MAX_TRANSIENT_MISSES = 5
    let transientMisses = 0
    drainPollRef.current = setInterval(async () => {
      try {
        const status = await debugDevHubDrainStatus(enumFlowId)
        notStartedMisses = 0
        transientMisses = 0
        setDrainStatus(status)
        if (status.done || status.stopped_on_error) {
          clearInterval(drainPollRef.current)
          drainPollRef.current = null
          setDraining(false)
          _clearLastDrain(enumFlowId)
        }
      } catch (err) {
        const notStartedYet = /^API 404/.test(err.message || '')
        if (notStartedYet && notStartedMisses < MAX_NOT_STARTED_MISSES) {
          notStartedMisses += 1
          return
        }
        if (!notStartedYet && transientMisses < MAX_TRANSIENT_MISSES) {
          transientMisses += 1
          return
        }
        clearInterval(drainPollRef.current)
        drainPollRef.current = null
        setDraining(false)
        setError(err.message || 'Could not read drain status')
      }
    }, 5000)
  }

  // `enumFlowIdArg` lets this be triggered automatically the instant a capture comes
  // back incomplete (from runStart/runComplete, using the response's own value directly
  // — `result` state isn't guaranteed updated yet at that point) as well as from the
  // manual "Resume" button (which has nothing but `result` state to read).
  async function resumeDrain(enumFlowIdArg) {
    const enumFlowId = enumFlowIdArg || result?.enum_flow_id
    if (!enumFlowId) return
    setDraining(true); setError(null)
    try {
      // Background mode (the backend's own default — omitting runInBackground here is
      // what selects it) so a large crawl (hundreds of categories, tens of batches)
      // doesn't sit inside one synchronous HTTP request long enough to hit Railway's
      // ~300s edge timeout. maxBatches raised well past the 50-batch default so this
      // covers a full population in the background without needing to be re-triggered.
      await debugDevHubDrain(enumFlowId, { maxBatches: 500 })
      _saveLastDrain(enumFlowId, customerId)
      watchDrain(enumFlowId)
    } catch (err) {
      setError(err.message || 'Drain failed to start')
      setDraining(false)
    }
  }

  async function checkDrainStatus() {
    if (!result?.enum_flow_id) return
    setError(null)
    try {
      const status = await debugDevHubDrainStatus(result.enum_flow_id)
      setDrainStatus(status)
    } catch (err) {
      // Same legitimate startup gap resumeDrain's auto-poll already tolerates
      // (item 91/95) -- the background drain's first checkpoint can take up to
      // batchSize x 60s (each category gets its own 60s timeout on the
      // backend), so a manual check clicked early hits the same expected 404
      // this route raises before then. A friendly note beats a raw API error.
      if (/^API 404/.test(err.message || '')) {
        setError(`Drain just started — no checkpoint yet (the first batch of ${batchSize} categories can take several minutes). Try again shortly.`)
      } else {
        setError(err.message || 'Could not read drain status')
      }
    }
  }

  // Manual recovery: check (and start watching) a drain by its enum_flow_id
  // directly, for when the page's own state AND its localStorage save are both
  // gone (e.g. a different browser, or a drain started before this recovery
  // feature existed). Never calls debug-dev-hub-drain -- only watchDrain, so
  // this can't spawn a second background task racing an already-running one.
  async function lookupDrain() {
    const enumFlowId = lookupEnumFlowId.trim()
    if (!enumFlowId) return
    setError(null)
    try {
      const status = await debugDevHubDrainStatus(enumFlowId)
      setResult({ enum_flow_id: enumFlowId, done: false, category_results: [] })
      setDrainStatus(status)
      if (!status.done && !status.stopped_on_error) watchDrain(enumFlowId)
    } catch (err) {
      setError(err.message || 'Could not find a drain with that ID')
    }
  }

  // Only watches an ALREADY-RUNNING probe onboarding run -- never (re-)starts one.
  // Split out of startProbeRun so a page reload can reattach to a run that's still
  // going server-side without calling run-probe-onboarding again, which would spawn
  // a second concurrent run racing the first over the same status checkpoint.
  function watchProbe(forCustomerId, forVendor, forProduct) {
    // Called from the customerId-select effect, startProbeRun, and (indirectly)
    // a reload's restore effect. Clearing any existing interval before starting
    // a new one (rather than refusing a second call outright) is what item 121
    // fixed live 2026-09-24: refusing is only safe if the existing interval is
    // still watching the right thing, and cancelling a run then starting a new
    // one in the same tab is exactly the case where it wasn't.
    if (probePollRef.current) clearInterval(probePollRef.current)
    // A single failed poll for ANY reason (a suspended tab, a dropped connection,
    // any transient network blip) used to kill this loop permanently, even though
    // the run itself is a server-side background task completely unaffected by
    // this browser tab's own network state. Confirmed live 2026-09-23:
    // net::ERR_NETWORK_IO_SUSPENDED (a Chrome/OS network suspension) killed a poll
    // and the page went silent for the rest of a run that was still going fine
    // server-side. Same tolerance already proven for the drain's own poll loop.
    // Only the STATUS fetch counts toward this -- it is what decides whether the
    // loop keeps going, so a transient miss on it must not be mistaken for "the
    // run finished". A failed history fetch is cosmetic (refreshProbe already
    // keeps the last-known-good history rather than blanking it) and never
    // affects this counter.
    const MAX_TRANSIENT_MISSES = 5
    let transientMisses = 0
    // ITEM 132, 2026-09-24: this used to fire an immediate priming read here,
    // "for instant feedback" -- removed; every caller already has fresh state
    // before calling watchProbe, so it only ever raced a slower write.
    //
    // ITEM 136, 2026-09-24: the SCHEDULED read below used to be its own
    // second copy of "GET status+history, setProbe" (fetchAndApply), separate
    // from refreshProbe above -- and Scott hit the same clobber-by-stale-read
    // shape anyway, from a caller that copy didn't share the fix with. Now
    // calls refreshProbe directly: one function, one guard, every caller
    // protected the same way instead of each caller needing its own patch
    // every time the race resurfaces somewhere new. (The guard itself was
    // wrong the first time -- see item 139, right where it's declared.)
    probePollRef.current = setInterval(async () => {
      let result
      try {
        result = await refreshProbe(forCustomerId, forVendor, forProduct)
        transientMisses = 0
      } catch (err) {
        if (transientMisses < MAX_TRANSIENT_MISSES) {
          transientMisses += 1
          return
        }
        clearInterval(probePollRef.current)
        probePollRef.current = null
        setProbeError(err.message || 'Could not read probe run status')
        return
      }
      // A stale (superseded) read is not evidence the run is done -- it
      // reflects a moment BEFORE whatever synthesized write superseded it.
      if (!result.stale && result.status?.done) {
        clearInterval(probePollRef.current)
        probePollRef.current = null
      }
    }, 5000)
  }

  // Runs the full probe harness (Stage 1-7) against this customer's live
  // connection, discover_bodies=true. `debug-probe-onboarding` writes an initial
  // {done:false} status almost immediately (unlike the drain, whose first
  // checkpoint waits on a whole batch), so no 404-tolerance dance is needed here.
  async function startProbeRun() {
    if (!customerId) { setProbeError('Pick a customer first.'); return }
    setStartingRun(true); setProbeError(null)
    try {
      const data = await runProbeOnboarding(customerId, probeVendor, probeProduct, { discoverBodies: true })
      // Scott, 2026-09-24: "a few seconds is not acceptable at all." Every
      // previous fix here (120, 124, 129) still waited on a GET, in some
      // form, to confirm the click worked -- and that GET can always race
      // FastAPI's BackgroundTasks, which schedules the actual run AFTER this
      // very POST response is sent. There is no polling interval short
      // enough to make that race go away; the fix is to stop needing the GET
      // at all for the very first update. This POST response ALREADY carries
      // everything needed, synchronously, in BOTH branches -- use it
      // directly instead of waiting on a follow-up read of the same fact.
      if (data?.started === false) {
        setProbeError(data.note || 'A run is already in progress for this customer -- not starting a new one.')
        // existing_status IS the current run's status -- the concurrency
        // guard read it moments ago to make this exact decision. No reason
        // to re-fetch what this response is already holding.
        if (data.existing_status) {
          setProbe(prev => ({ ...prev, status: data.existing_status }))
        }
      } else if (data?.started && data?.run_id) {
        // We just started this run -- known, synchronously, the instant this
        // resolves. Synthesize its status NOW so the button reads "Running…"
        // immediately, not whenever a subsequent poll happens to land after
        // the background task's own first write. watchProbe's polling still
        // runs from here on to pick up REAL progress as it happens.
        //
        // item 139 -- see pendingNewRunIdRef's declaration: the server can
        // take several poll cycles to actually catch up to this run_id, and
        // every read before it does must be discarded, not just the first.
        pendingNewRunIdRef.current = data.run_id
        setProbe(prev => ({
          ...prev,
          status: { done: false, run_id: data.run_id, started_at: Date.now() / 1000 },
        }))
      }
      watchProbe(customerId, probeVendor, probeProduct)
    } catch (err) {
      setProbeError(err.message || 'Probe run failed to start')
    } finally {
      setStartingRun(false)
    }
  }

  // drainStatus reflects the background drain's cumulative progress and is
  // strictly newer than `result` (the one-shot response from BEFORE draining
  // even started) once it exists -- checking `result` first left this table
  // frozen on the very first request's small snapshot for the entire drain,
  // no matter how many more categories the background job actually attempted.
  const categoryResults = drainStatus?.category_results || result?.category_results || []
  const effectiveDone = drainStatus?.done ?? result?.done
  const effectiveRemaining = drainStatus?.categories_remaining ?? result?.categories_remaining
  // A result with steps_log but no categories_available never reached the category
  // scrape at all -- it stopped mid-way through the Main Menu -> Administration ->
  // Developer Portal click-through inside the WFM tenant UI. That's a different,
  // more useful failure to show than "no categories matched" (which implies the
  // scrape ran and found nothing, not that it never started).
  const navigationStalled = result && Array.isArray(result.steps_log) && result.categories_available == null

  const breadcrumbs = [
    { label: 'Admin', href: '/admin' },
    { label: 'Developer Hub Capture' },
  ]
  const navActions = <Link to="/admin" className="nav-btn">← Dashboard</Link>
  // Local names for the render below -- `probe` is the single state object
  // (see its declaration above), these are read-only views into it.
  const probeStatus = probe.status
  const probeHistory = probe.history

  return (
    <>
      <TopNav breadcrumbs={breadcrumbs} actions={navActions} />
      <div className="dhc-page">
        <div className="dhc-header">
          <h1 className="dhc-title">Developer Hub Capture</h1>
          <p className="dhc-subtitle">
            Drills a customer's UKG Developer Hub docs site and downloads the OpenAPI spec
            behind every category — the same mechanism that found "User Preferences" nested
            inside Common Resources II. Leave <code>target_categories</code> blank to crawl
            the ENTIRE population (item 90 — no more 100-category cap); a batch runs
            immediately and anything left over continues automatically in the background,
            no further clicks needed. Set <code>target_categories</code> only to jump
            straight at a known lead instead (comma-separated, case-insensitive — unmatched
            names are silently skipped).
          </p>
        </div>

        {error && <div className="dhc-error-banner">{error}</div>}

        {!result && (
          <div className="dhc-resume-bar">
            <span className="dhc-resume-note">
              Lost track of a running drain (closed the page, different browser)? A drain
              keeps running server-side regardless — paste its ID to reattach and watch it.
            </span>
            <input
              className="dhc-input dhc-mono"
              type="text"
              placeholder="enum_flow_id, e.g. 9d6114e8-3a06-4cdf-8255-adf7addfdab8"
              value={lookupEnumFlowId}
              onChange={e => setLookupEnumFlowId(e.target.value)}
            />
            <button className="nav-btn" onClick={lookupDrain}>Check this drain</button>
          </div>
        )}

        <div className="dhc-form">
          <div className="dhc-field">
            <label className="dhc-label">Customer</label>
            <select className="dhc-input" value={customerId} onChange={e => setCustomerId(e.target.value)}>
              <option value="">Select a customer…</option>
              {customers.map(c => (
                <option key={c.customer_id || c.id} value={c.customer_id || c.id}>
                  {c.company_name || c.name || c.customer_id}
                </option>
              ))}
            </select>
            {customersError && <LoadError inline what="customers" error={customersError} onRetry={reloadCustomers} />}
          </div>

          <div className="dhc-field dhc-field-wide">
            <label className="dhc-label">Target categories (optional — leave blank to crawl in page order)</label>
            <input
              className="dhc-input"
              type="text"
              placeholder="e.g. Scheduling Setup, Staffing Matrices, Staffing Setup, Scheduling"
              value={targetCategories}
              onChange={e => setTargetCategories(e.target.value)}
              disabled={needsMfa}
            />
          </div>

          <div className="dhc-field">
            <label className="dhc-label">Batch size</label>
            <input
              className="dhc-input"
              type="number"
              min={1}
              max={100}
              value={batchSize}
              onChange={e => setBatchSize(Number(e.target.value) || 10)}
              disabled={needsMfa}
            />
          </div>

          <div className="dhc-field dhc-field-actions">
            <button className="nav-btn primary" onClick={runStart} disabled={capturing || needsMfa}>
              {capturing ? 'Starting… (logs in and drives a real browser, can take a minute)' : 'Capture'}
            </button>
            <label className="dhc-advanced-toggle">
              <input type="checkbox" checked={advancedMode}
                     onChange={e => setAdvancedMode(e.target.checked)} disabled={needsMfa} />
              Advanced: use a separate Developer Hub login instead
            </label>
          </div>

          {advancedMode && (
            <>
              <div className="dhc-field">
                <label className="dhc-label">Product docs root</label>
                <select className="dhc-input" value={docPath} onChange={e => setDocPath(e.target.value)}>
                  <option value="/wfm/reference">WFM (UKG_PRO_WFM_REST)</option>
                  <option value="/hcm/reference">HCM (UKG_PRO_REST)</option>
                </select>
              </div>
              <div className="dhc-field">
                <label className="dhc-label">Tenant hostname (optional)</label>
                <input
                  className="dhc-input" type="text" placeholder="improves base_url classification"
                  value={hostname} onChange={e => setHostname(e.target.value)}
                />
              </div>
              <p className="dhc-advanced-note">
                Requires a Developer Hub Username/Password saved on this connection
                (<code>/customers/{customerId || '…'}/connectors</code>) — falls back to the
                connection's regular login when unset, same as the default path above.
              </p>
            </>
          )}
        </div>

        {needsMfa && (
          <div className="dhc-mfa-bar">
            <span className="dhc-mfa-note">
              MFA code sent — check the inbox for this connection's login, then paste the code below.
            </span>
            <input
              className="dhc-input dhc-mfa-input" type="text" placeholder="123456"
              value={mfaCode} onChange={e => setMfaCode(e.target.value)}
            />
            <button className="nav-btn primary" onClick={runComplete} disabled={completing}>
              {completing ? 'Completing…' : 'Submit code'}
            </button>
          </div>
        )}

        {result && !navigationStalled && (
          <div className="dhc-summary">
            <div className="dhc-summary-item">
              <span className="dhc-summary-value">{result.categories_attempted ?? 0}</span>
              <span className="dhc-summary-label">Attempted</span>
            </div>
            <div className="dhc-summary-item">
              <span className="dhc-summary-value">{result.categories_available ?? '—'}</span>
              <span className="dhc-summary-label">Available on page</span>
            </div>
            <div className="dhc-summary-item">
              <span className="dhc-summary-value dhc-summary-value-good">
                {drainStatus?.unique_specs_found_total ?? result.unique_specs_found ?? 0}
              </span>
              <span className="dhc-summary-label">Specs actually saved</span>
            </div>
            <div className="dhc-summary-item">
              <span className={`dhc-summary-value ${effectiveDone ? 'dhc-summary-value-good' : 'dhc-summary-value-warn'}`}>
                {effectiveDone ? 'Done' : `${effectiveRemaining ?? '?'} remaining`}
              </span>
              <span className="dhc-summary-label">Status</span>
            </div>
          </div>
        )}

        {result && !effectiveDone && result.enum_flow_id && (
          <div className="dhc-resume-bar">
            <span className="dhc-resume-note">
              {draining && drainStatus
                ? `Draining in the background — ${drainStatus.batches_run ?? 0} batch(es) run, `
                  + `${drainStatus.categories_attempted_total ?? 0} categories attempted, `
                  + `${drainStatus.unique_specs_found_total ?? 0} unique specs found so far, `
                  + `${drainStatus.categories_remaining ?? '?'} remaining…`
                : <>Not everything fit in one request — <code>{result.enum_flow_id}</code> is checkpointed and safe to resume.</>}
            </span>
            <button className="nav-btn" onClick={resumeDrain} disabled={draining}>
              {draining ? 'Draining…' : 'Resume (drain remaining)'}
            </button>
            <button className="nav-btn" onClick={checkDrainStatus}>Check status</button>
          </div>
        )}

        {categoryResults.length > 0 && (
          <>
            <div className="dhc-field-actions">
              <button
                className="nav-btn"
                onClick={() => _exportRowsToExcel(
                  `dev-hub-capture-${customerId || 'export'}.xlsx`,
                  categoryResults.map(r => ({
                    category: r.category,
                    status: r.spec_storage_path && !r.spec_storage_error ? 'saved'
                      : r.duplicate_of_category ? 'duplicate'
                      : r.click_error || r.force_click_error ? 'click failed'
                      : r.download_failure_reason ? 'download failed'
                      : r.oas_spec_path ? 'downloaded, not saved'
                      : r.error ? 'error' : 'no spec',
                    storage_path: r.spec_storage_path || (r.duplicate_of_category ? r.oas_spec_path : '') || '',
                    duplicate_of_category: r.duplicate_of_category || '',
                    error: r.error || r.spec_storage_error || r.spec_classification_error
                      || r.click_error || r.force_click_error || r.download_failure_reason || '',
                  })),
                  'Categories'
                )}
              >
                Export to Excel
              </button>
            </div>
            <table className="dhc-table">
            <thead>
              <tr>
                <th>Category</th>
                <th>Spec saved?</th>
                <th>Storage path</th>
                <th>Error</th>
              </tr>
            </thead>
            <tbody>
              {categoryResults.map((r, i) => (
                <tr key={i}>
                  <td>{r.category}</td>
                  <td>
                    {r.spec_storage_path && !r.spec_storage_error ? (
                      <span className="dhc-badge dhc-badge-good">saved</span>
                    ) : r.duplicate_of_category ? (
                      // Many Developer Hub sidebar category labels resolve to the
                      // SAME underlying OAS spec (e.g. a whole family of
                      // "Activity ___" pages all download activities.json) --
                      // spec_path_first_seen_by (connector.py:7075-7090,
                      // :7673-7687) correctly skips re-uploading a spec already
                      // captured earlier in this batch. That's working as
                      // intended, not a failure -- rendering it as "downloaded,
                      // not saved" (its previous fallback) made correct dedup
                      // look identical to a real problem.
                      <span className="dhc-badge">duplicate</span>
                    ) : r.click_error || r.force_click_error ? (
                      // oas_spec_path is read from the Download button's own
                      // data-raycast-oas-path attribute the instant the button is
                      // located -- BEFORE any click is attempted -- so it can be
                      // truthy even when the click itself then fails twice
                      // (normal click, then a forced click) and nothing ever
                      // downloads. Check this before oas_spec_path below, or a
                      // real click failure renders as a false "downloaded" state
                      // with no visible error.
                      <span className="dhc-badge dhc-badge-bad">click failed</span>
                    ) : r.download_failure_reason ? (
                      // Click succeeded (no click_error/force_click_error) but
                      // Playwright's download.path() still came back empty --
                      // the browser-side download itself failed. download.
                      // failure() gives the actual reason instead of leaving
                      // this indistinguishable from a genuine "downloaded, not
                      // saved" storage problem.
                      <span className="dhc-badge dhc-badge-bad">download failed</span>
                    ) : r.oas_spec_path ? (
                      <span className="dhc-badge dhc-badge-warn">downloaded, not saved</span>
                    ) : r.error ? (
                      <span className="dhc-badge dhc-badge-bad">error</span>
                    ) : (
                      <span className="dhc-badge">no spec</span>
                    )}
                  </td>
                  <td className="dhc-mono dhc-muted">
                    {r.spec_storage_path || (r.duplicate_of_category ? r.oas_spec_path : null) || '—'}
                  </td>
                  <td className="dhc-muted">
                    {r.duplicate_of_category
                      ? `same spec as "${r.duplicate_of_category}"`
                      : (r.error || r.spec_storage_error || r.spec_classification_error
                          || r.click_error || r.force_click_error || r.download_failure_reason || '—')}
                  </td>
                </tr>
              ))}
            </tbody>
            </table>
          </>
        )}

        {navigationStalled && (
          <>
            <div className="dhc-empty-note">
              Never reached the category scrape — the click-through inside the WFM tenant UI
              (Main Menu → Administration → Developer Portal) stopped partway. See exactly
              which step failed below.
            </div>
            <table className="dhc-table">
              <thead>
                <tr><th>Step</th><th>Matched text</th><th>Clicked?</th></tr>
              </thead>
              <tbody>
                {result.steps_log.map((s, i) => (
                  <tr key={i}>
                    <td>{s.step}</td>
                    <td className="dhc-mono">{s.matched_text || '—'}</td>
                    <td>
                      {s.clicked
                        ? <span className="dhc-badge dhc-badge-good">yes</span>
                        : <span className="dhc-badge dhc-badge-bad">no</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {result.nav_candidates?.length > 0 && (
              <div className="dhc-empty-note" style={{ textAlign: 'left' }}>
                Real menu text found on the page where it stopped:{' '}
                {result.nav_candidates.map(t => `"${t}"`).join(', ')}
              </div>
            )}
            {result.api_specifications_href_found && (
              <div className="dhc-empty-note" style={{ textAlign: 'left' }}>
                Found an "API Specifications" link (href: <code>{result.api_specifications_href_found}</code>)
                and tried navigating to it directly before falling back to a click.
                {result.api_specifications_goto_error
                  ? <> That navigation failed: <span className="dhc-mono">{result.api_specifications_goto_error}</span></>
                  : ' That succeeded, but the click fallback above still ran and failed.'}
              </div>
            )}
            {!result.api_specifications_href_found && (
              <div className="dhc-empty-note" style={{ textAlign: 'left' }}>
                No "API Specifications" link with an href was found on the page at all —
                the click attempt above was the only thing tried.
              </div>
            )}
            {result.dev_hub_candidates?.length > 0 && (
              <div className="dhc-empty-note" style={{ textAlign: 'left' }}>
                Real links/buttons on the page it stopped on:{' '}
                {result.dev_hub_candidates.slice(0, 30).map(c => `"${c.text}"${c.href ? ` (${c.href})` : ''}`).join(', ')}
              </div>
            )}
            {result.sso_diag && (
              <div className="dhc-empty-note" style={{ textAlign: 'left' }}>
                Community login step-by-step (what actually happened logging into the
                Developer Hub, before it ever got to looking for API Specifications):
                <pre className="dhc-mono" style={{ whiteSpace: 'pre-wrap', marginTop: 8 }}>
                  {JSON.stringify(result.sso_diag, null, 2)}
                </pre>
              </div>
            )}
          </>
        )}

        {result && !navigationStalled && categoryResults.length === 0 && (
          <div className="dhc-empty-note">
            No categories matched — nothing was drilled. If you used target_categories, check the
            exact label as it reads on the Developer Hub left-nav and try again.
          </div>
        )}

        <div className="dhc-header" style={{ marginTop: 32 }}>
          <h1 className="dhc-title" style={{ fontSize: '1.3rem' }}>Run Probe Onboarding (Stage 7 — Body Discovery)</h1>
          <p className="dhc-subtitle">
            A separate step from capturing specs above — this runs the full probe harness
            against the customer's live connection (auth, endpoint classification, shape
            capture, and Stage 7 body discovery). <code>discover_bodies=true</code> converses
            with every endpoint the run classifies <code>needs_body</code>, now seeded from
            this customer's own captured Developer Hub specs before any live vendor refusal
            is even needed. Works identically for any connection this customer has — pick
            below which one to run it against. Requires a customer picked above.
          </p>
        </div>

        {connectionsError && (
          <LoadError inline what="this customer's connections" error={connectionsError} onRetry={() => setConnectionsTry(n => n + 1)} />
        )}
        {connections.length > 0 && (
          <div className="dhc-field">
            <label className="dhc-label">Run against</label>
            <select
              className="dhc-input"
              value={`${probeVendor}::${probeProduct}`}
              onChange={e => {
                const [v, p] = e.target.value.split('::')
                setProbeVendor(v); setProbeProduct(p)
              }}
              disabled={probeRunning || startingRun}
            >
              {connections.map(c => (
                <option key={`${c.vendor_id}::${c.product_id}`} value={`${c.vendor_id}::${c.product_id}`}>
                  {c.vendor_id} — {c.product_id}
                </option>
              ))}
            </select>
          </div>
        )}

        {probeError && <div className="dhc-error-banner">{probeError}</div>}

        <div className="dhc-field-actions">
          <button className="nav-btn primary" onClick={startProbeRun} disabled={probeRunning || startingRun}>
            {startingRun ? 'Starting…'
              : probeRunning ? 'Running… (can take several minutes)' : 'Run Probe Onboarding'}
          </button>
        </div>

        {/* Real, in-platform visibility, not inferred from this tab's own polling
            state -- fetched directly from the server's persistent run history
            (routers/connector.py's debug_probe_onboarding_history) whenever a
            customer is selected. Answers "what's running right now" and "did
            anything race" directly, without a console command. */}
        {probeHistory?.in_flight?.length > 0 && (
          <div className={`dhc-summary ${probeHistory.in_flight.length > 1 ? 'dhc-error-banner' : ''}`}>
            {probeHistory.in_flight.length > 1 && (
              <div style={{ width: '100%', marginBottom: 8, fontWeight: 600 }}>
                {probeHistory.in_flight.length} runs are active at once for this
                customer — they are racing each other over the same result.
              </div>
            )}
            {probeHistory.in_flight.map(r => {
              // If the live status poll's CURRENT read is for this exact row,
              // show its real stage_progress detail instead of just a
              // duration. This is the single place a tracked run's progress
              // renders -- not a second, separate tile below (see the legacy
              // fallback further down): two tiles for the same run, each
              // with its own Cancel button, was Scott's exact 2026-09-24
              // report ("this is out of control").
              const live = probeStatus && !probeStatus.done && probeStatus.run_id === r.run_id
                ? probeStatus : null
              return (
                <div className="dhc-summary-item" key={r.run_id}>
                  <span className="dhc-summary-value">
                    {live?.stage_progress
                      ? `${live.stage_progress.done} / ${live.stage_progress.total}`
                      : _fmtDuration(r.started_at, null)}
                  </span>
                  <span className="dhc-summary-label">
                    {live?.stage_progress
                      ? `${live.stage || 'running'} — ${live.stage_progress.current || '…'}`
                      : `active since ${_fmtClock(r.started_at)} — ${(r.run_id || '').slice(0, 8)}`}
                  </span>
                  <button
                    className="nav-btn" style={{ marginTop: 6 }}
                    disabled={cancellingKeys.has(r.run_id)}
                    onClick={() => cancelRun(r.run_id, r.started_at)}
                  >
                    {cancellingKeys.has(r.run_id) ? 'Cancelling…' : 'Cancel'}
                  </button>
                </div>
              )
            })}
          </div>
        )}

        {probeHistory?.history?.length > 0 && (
          <table className="dhc-table">
            <thead>
              <tr>
                <th>Run</th>
                <th>Started</th>
                <th>Duration</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {[...probeHistory.history].reverse().map(r => (
                <tr key={r.run_id}>
                  <td className="dhc-muted">{(r.run_id || '').slice(0, 8)}</td>
                  <td>{_fmtClock(r.started_at)}</td>
                  <td>{_fmtDuration(r.started_at, r.finished_at)}</td>
                  <td>
                    <span className={`dhc-badge ${
                      r.done === false ? 'dhc-badge-warn' : r.error ? 'dhc-badge-warn' : 'dhc-badge-good'
                    }`}>
                      {r.done === false ? 'running' : r.error ? `error: ${String(r.error).slice(0, 60)}` : 'done'}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {/* Fallback for a run not (yet) reflected in `in_flight` -- a truly
            legacy run with no run_id at all (item 118's original case), OR
            (item 130) a run this tab just started/is watching whose status
            has no `stage_progress` yet, since startProbeRun synthesizes a
            minimal status immediately from the POST response, before any
            real progress exists to report. Gated strictly on `in_flight`
            being EMPTY, not on a per-row run_id match or on stage_progress
            being present -- Scott, 2026-09-24: requiring stage_progress
            meant a freshly-started run showed "Running…" on the button with
            NO tile and no Cancel button at all until the first real poll
            landed. Now always renders something the instant probeRunning is
            true, with a Cancel button, regardless of how much detail is in
            yet. */}
        {!probeHistory?.in_flight?.length && probeStatus && !probeStatus.done && (
          <div className="dhc-summary">
            <div className="dhc-summary-item">
              <span className="dhc-summary-value">
                {probeStatus.stage_progress
                  ? `${probeStatus.stage_progress.done} / ${probeStatus.stage_progress.total}`
                  : _fmtDuration(probeStatus.started_at, null)}
              </span>
              <span className="dhc-summary-label">
                {probeStatus.stage_progress
                  ? `${probeStatus.stage || 'running'} — ${probeStatus.stage_progress.current || '…'}`
                  : 'starting…'}
              </span>
              <button
                className="nav-btn" style={{ marginTop: 6 }}
                disabled={cancellingKeys.has(probeStatus.run_id || `legacy-${probeStatus.started_at}`)}
                onClick={() => cancelRun(probeStatus.run_id, probeStatus.started_at)}
              >
                {cancellingKeys.has(probeStatus.run_id || `legacy-${probeStatus.started_at}`)
                  ? 'Cancelling…' : 'Cancel'}
              </button>
            </div>
          </div>
        )}

        {probeStatus && (
          <>
            {/* Scott, 2026-09-25, after confirming the Schema Diff page's plain-English
                text was good: "Do the same for the probe harness stuff as well." Open by
                default, same as that page's panel -- explains endpoints_probed/
                ready_to_sync and translates whichever raw classification labels
                (works, needs_body, permission, ...) this run actually produced,
                instead of a hover tooltip nobody would think to check. */}
            <details className="dhc-explainer" open>
              <summary className="dhc-explainer-summary">What does this mean?</summary>
              <div className="dhc-explainer-body">
                <p>
                  <strong>endpoints_probed</strong> is how many endpoints this run reached
                  at all. <strong>ready_to_sync</strong> is how many of those are proven to
                  work right now, with real data back from the vendor.
                </p>
                {Object.keys(probeStatus.summary?.by_class || {}).length > 0 && (
                  <ul>
                    {Object.keys(probeStatus.summary.by_class).map(classification => (
                      <li key={classification}>
                        <strong>{classification}</strong>
                        {' — '}
                        {classificationPlain(classification) || 'No plain-English translation yet for this classification.'}
                      </li>
                    ))}
                  </ul>
                )}
                <p>
                  In the table below, <strong>Outcome</strong> is what Stage 7 Body
                  Discovery concluded after conversing with the vendor for a{' '}
                  <code>needs_body</code> endpoint (see the sentence under each badge).{' '}
                  <strong>Unresolved</strong> lists any parameter name the vendor asked for
                  that nothing could derive a value for — a real question for a human, not
                  a bug. <strong>Why</strong> is the harness's own plain-English account of
                  what happened, in its own words — this used to only be visible in the
                  Excel export.
                </p>
              </div>
            </details>

            <div className="dhc-summary">
              {typeof probeStatus.summary?.endpoints_probed === 'number' && (
                <div className="dhc-summary-item">
                  <span className="dhc-summary-value">{probeStatus.summary.endpoints_probed}</span>
                  <span className="dhc-summary-label">endpoints_probed</span>
                </div>
              )}
              {typeof probeStatus.summary?.ready_to_sync === 'number' && (
                <div className="dhc-summary-item">
                  <span className="dhc-summary-value dhc-summary-value-good">{probeStatus.summary.ready_to_sync}</span>
                  <span className="dhc-summary-label">ready_to_sync</span>
                </div>
              )}
              {/* React error #31 (2026-09-23 live crash): summary itself is
                  {auth, endpoints_probed, ready_to_sync, by_class}, NOT a flat
                  classification->count map -- the per-classification counts this
                  page means to show live one level deeper, in by_class. Iterating
                  summary directly tried to render the whole by_class OBJECT as a
                  child. */}
              {Object.entries(probeStatus.summary?.by_class || {}).map(([classification, count]) => (
                <div className="dhc-summary-item" key={classification}>
                  <span className="dhc-summary-value">{count}</span>
                  <span className="dhc-summary-label">{classification}</span>
                </div>
              ))}
            </div>
          </>
        )}

        {probeStatus?.done && Array.isArray(probeStatus.body_discoveries) && (
          <>
            <div className="dhc-field-actions">
              <button
                className="nav-btn"
                onClick={() => _exportSheetsToExcel(
                  `probe-onboarding-${customerId || 'export'}.xlsx`,
                  [
                    {
                      name: 'Summary',
                      rows: Object.entries(probeStatus.summary?.by_class || {}).map(([classification, count]) => ({
                        classification, count,
                      })),
                    },
                    {
                      name: 'Body Discoveries',
                      rows: probeStatus.body_discoveries.map(d => ({
                        endpoint: d.endpoint,
                        outcome: d.outcome,
                        unresolved: (d.unresolved || []).join(', '),
                        clarifications: (d.clarifications || []).join(' | '),
                        // Item 152's investigation (a probe export, 2026-09-26): tracing
                        // whether item 150's fan-out budget fix actually tried up to
                        // max_fanout_members real candidates needed to see WHICH members
                        // were tried and in what order -- data BodyDiscovery.to_dict()
                        // already carries (fanout_attempts, rounds) and the live status
                        // JSON already sends, but this export only ever kept the four
                        // summary fields above. Re-exporting an ALREADY-FINISHED run
                        // (reload this page, no new probe run needed -- the full data is
                        // still sitting in Supabase Storage under debug-probe-onboarding-
                        // status) now surfaces it instead of requiring a fresh live run
                        // or F12 just to see which fan-out members were actually tried.
                        fanout_attempts: JSON.stringify(d.fanout_attempts || {}),
                        param_sources: JSON.stringify(d.param_sources || {}),
                      })),
                    },
                    {
                      name: 'Rounds',
                      rows: probeStatus.body_discoveries.flatMap(d =>
                        (d.rounds || []).map(r => ({
                          endpoint: d.endpoint,
                          round: r.round,
                          status: r.status,
                          slot: r.slot,
                          rows_returned: r.rows,
                          sent_keys: (r.sent_keys || []).join(', '),
                          named: (r.named || []).join(', '),
                          new_names: (r.new_names || []).join(', '),
                          filled: (r.filled || []).join(', '),
                          error_excerpt: r.error_excerpt,
                        }))
                      ),
                    },
                  ]
                )}
              >
                Export to Excel
              </button>
            </div>
            <table className="dhc-table">
            <thead>
              <tr>
                <th>Endpoint</th>
                <th>Outcome</th>
                <th>Unresolved</th>
                <th>Why</th>
              </tr>
            </thead>
            <tbody>
              {probeStatus.body_discoveries.map((d, i) => (
                <tr key={i}>
                  <td>{d.endpoint}</td>
                  <td>
                    <span className={`dhc-badge ${d.outcome === 'resolved' ? 'dhc-badge-good' : 'dhc-badge-warn'}`}>
                      {d.outcome}
                    </span>
                    {stage7OutcomePlain(d.outcome) && (
                      <p className="dhc-outcome-plain">{stage7OutcomePlain(d.outcome)}</p>
                    )}
                  </td>
                  <td className="dhc-muted">{(d.unresolved || []).join(', ') || '—'}</td>
                  <td>
                    {/* Scott, 2026-09-25: "Do the same for the probe harness stuff as
                        well." This text (body_loop.py's own result.clarifications) was
                        already plain English -- it just used to be visible ONLY in the
                        Excel export, never on the page itself, which is why every live
                        trace this session started from Scott re-uploading that export. */}
                    {(d.clarifications || []).length > 0 ? (
                      <ul className="dhc-clarifications">
                        {d.clarifications.map((c, ci) => <li key={ci}>{c}</li>)}
                      </ul>
                    ) : <span className="dhc-muted">—</span>}
                  </td>
                </tr>
              ))}
            </tbody>
            </table>
          </>
        )}

        {probeStatus?.done && probeStatus.error && (
          <div className="dhc-error-banner">{probeStatus.error}</div>
        )}
      </div>
    </>
  )
}
